import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { z } from 'zod';

import { useCreateIssueReport } from '@/api/generated/support.ts';
import { useGetMe } from '@/api/generated/users.ts';
import { useListWorkspaces } from '@/api/generated/workspaces.ts';
import Button from '@/core/components/Button.tsx';
import DescriptionList from '@/core/components/DescriptionList.tsx';
import Dialog from '@/core/components/Dialog.tsx';
import Disclosure from '@/core/components/Disclosure.tsx';
import { UnexpectedErrorActionContext } from '@/core/components/ErrorMessage.tsx';
import Form from '@/core/components/Form.tsx';
import TextField from '@/core/components/TextField.tsx';
import { clearErrorLog, readErrorLog } from '@/core/errorLog.ts';
import { requiredText } from '@/utils/forms.ts';
import { zodResolver } from '@hookform/resolvers/zod';

// What the API takes (support/schemas.py).
const MAX_ERROR_LOG_LENGTH = 20000;

const reportSchema = z.object({ description: requiredText });
type ReportValues = z.input<typeof reportSchema>;
type ReportOutput = z.output<typeof reportSchema>;

// The browser and system from the user agent, e.g. "Chrome 141" and "macOS"; the full one goes along unchanged.
const BROWSERS: [string, RegExp][] = [
    ['Edge', /Edg\/(\d+)/],
    ['Firefox', /Firefox\/(\d+)/],
    ['Chrome', /Chrome\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari/],
];
const SYSTEMS: [string, RegExp][] = [
    ['iOS', /iPhone|iPad/],
    ['Android', /Android/],
    ['macOS', /Mac OS X/],
    ['Windows', /Windows/],
    ['Linux', /Linux/],
];

const describeBrowser = (userAgent: string) => {
    const browser = BROWSERS.map(([name, pattern]) => [name, userAgent.match(pattern)?.[1]] as const).find(
        ([, version]) => version
    );
    const system = SYSTEMS.find(([, pattern]) => pattern.test(userAgent))?.[0];
    return { browser: browser ? `${browser[0]} ${browser[1]}` : null, system: system ?? null };
};

const ReportProblemForm = ({ onClose }: { onClose: () => void }) => {
    const { t } = useTranslation();
    const user = useGetMe();
    const { workspaceSlug } = useParams();
    const workspace = useListWorkspaces().data?.find((item) => item.slug === workspaceSlug);
    const createIssueReport = useCreateIssueReport();
    const [isSent, setIsSent] = useState(false);
    // Read once at opening, so what's shown is what's sent.
    const [errorLog] = useState(() => readErrorLog(MAX_ERROR_LOG_LENGTH));
    const [pageUrl] = useState(() => window.location.href);
    const { browser, system } = describeBrowser(navigator.userAgent);
    const form = useForm<ReportValues, unknown, ReportOutput>({
        resolver: zodResolver(reportSchema),
        defaultValues: { description: '' },
    });

    const handleSubmit = async (values: ReportOutput) => {
        await createIssueReport.mutateAsync({
            data: {
                description: values.description,
                error_log: errorLog.text,
                page_url: pageUrl,
                workspace_id: workspace?.id ?? null,
            },
        });
        clearErrorLog();
        setIsSent(true);
    };

    if (isSent) {
        return (
            <>
                <Dialog.Title>{t('support.sentTitle')}</Dialog.Title>
                <Dialog.Body>
                    <Dialog.Description>{t('support.sent')}</Dialog.Description>
                </Dialog.Body>
                <Dialog.Footer>
                    <Button variant="primary" onClick={onClose}>
                        {t('common.close')}
                    </Button>
                </Dialog.Footer>
            </>
        );
    }

    return (
        <Dialog.Form
            form={form}
            onSubmit={handleSubmit}
            title={t('support.title')}
            description={t('support.description')}
            submitLabel={t('support.submit')}
            onCancel={onClose}
        >
            <Form.Field name="description">
                <TextField label={t('support.whatHappened')} rows={5} isRequired autoFocus />
            </Form.Field>
            <div>
                <p className="text-sm font-medium">{t('support.sentAlong')}</p>
                <DescriptionList className="text-sm">
                    <DescriptionList.Item label={t('support.name')}>{user.data?.display_name}</DescriptionList.Item>
                    <DescriptionList.Item label={t('support.email')}>{user.data?.email}</DescriptionList.Item>
                    {workspace && (
                        <DescriptionList.Item label={t('support.workspace')}>
                            {t('support.workspaceWithRole', {
                                workspace: workspace.name,
                                role: t(`support.roles.${workspace.role}`),
                            })}
                        </DescriptionList.Item>
                    )}
                    <DescriptionList.Item label={t('support.page')}>{pageUrl}</DescriptionList.Item>
                    <DescriptionList.Item label={t('support.browser')}>
                        {browser && system ? t('support.browserOnSystem', { browser, system }) : (browser ?? system)}
                    </DescriptionList.Item>
                </DescriptionList>
            </div>
            {errorLog.count > 0 && (
                <Disclosure title={t('support.errorLog')} summary={t('support.errorCount', { count: errorLog.count })}>
                    <pre className="mt-2 max-h-60 overflow-auto rounded-lg bg-surface-muted p-3 font-mono text-xs whitespace-pre-wrap">
                        {errorLog.text}
                    </pre>
                </Disclosure>
            )}
        </Dialog.Form>
    );
};

interface ReportProblemDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

// Telling us about a problem (plan task 98), with who's sending it, from where and the tab's recent unexpected errors.
// Kept out of the URL (plan/docs/structure.md "Dialogs"): it opens over an error page, which a new URL would render
// again without the error, and its log is only in memory.
const ReportProblemDialog = ({ isOpen, onClose }: ReportProblemDialogProps) => (
    <Dialog isOpen={isOpen} onClose={onClose}>
        {/* Its own failure isn't offered to report from in here. */}
        <UnexpectedErrorActionContext value={null}>
            <ReportProblemForm onClose={onClose} />
        </UnexpectedErrorActionContext>
    </Dialog>
);

export default ReportProblemDialog;

// For the generic error's notice and the error page.
export const ReportProblemButton = ({ size = 'sm' }: { size?: 'sm' | 'md' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <Button size={size} onClick={() => setIsOpen(true)}>
                {t('support.title')}
            </Button>
            <ReportProblemDialog isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </>
    );
};
